import Auth from '@aws-amplify/auth'
import { callWithRetry } from './retry'

/**
 * Cognito error codes that mean the stored credentials are permanently unusable
 * (refresh token expired / revoked / user disabled). Anything else is treated as transient.
 */
const DEFINITIVE_AUTH_CODES = [
  'NotAuthorizedException',
  'UserNotFoundException',
  'UserNotConfirmedException',
  'PasswordResetRequiredException',
]

/**
 * Amplify v5 rejects with *strings* (not Errors) when no user is stored.
 */
const NO_SESSION_MESSAGES = [
  'No current user',
  'The user is not authenticated',
  'No userPool',
  'Invalid session',
]

const errorText = (err: any): string =>
  typeof err === 'string' ? err : String(err?.message ?? '')

/**
 * True only when we are certain the user has no usable session. Network errors, timeouts,
 * throttling and 5xx from Cognito or our API must NOT land here — a refresh that merely
 * could not complete is not a logout.
 */
export const isDefinitiveAuthFailure = (err: any): boolean => {
  if (!err) return false
  if (DEFINITIVE_AUTH_CODES.includes(err.code ?? err.name)) return true
  const text = errorText(err)
  return NO_SESSION_MESSAGES.some(m => text.includes(m))
}

/** True for an HTTP response that says our credentials were rejected. */
export const isUnauthorizedResponse = (err: any): boolean => {
  const status = err?.response?.status
  return status === 401 || status === 403
}

let sessionInFlight: Promise<any> | null = null

/**
 * `Auth.currentSession()` with concurrent callers coalesced. When the idToken is expired
 * every caller would otherwise start its own Cognito refresh (the page load fires several
 * API calls at once through the axios interceptor).
 */
export const currentSessionShared = (): Promise<any> => {
  if (!sessionInFlight) {
    sessionInFlight = Auth.currentSession().finally(() => {
      sessionInFlight = null
    })
  }
  return sessionInFlight
}

export type SessionResult =
  | { status: 'authenticated'; session: any }
  | { status: 'unauthenticated' }
  | { status: 'unknown'; error: any }

const SESSION_RETRY = {
  maxRetries: 3,
  waitBetweenTries: 500,
  timeout: 10000,
  retryCondition: (err: any) => !isDefinitiveAuthFailure(err),
}

/**
 * Resolves the current Cognito session (silently refreshing an expired idToken) into one of
 * three outcomes. Callers must treat `unknown` as "keep the last known state", never as
 * signed out.
 */
export const resolveSession = async (): Promise<SessionResult> => {
  try {
    const session = await callWithRetry(
      () => currentSessionShared(),
      SESSION_RETRY
    )
    return session?.isValid()
      ? { status: 'authenticated', session }
      : { status: 'unauthenticated' }
  } catch (error) {
    return isDefinitiveAuthFailure(error)
      ? { status: 'unauthenticated' }
      : { status: 'unknown', error }
  }
}
